import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

export interface KlvWindowPortalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  onReady?: () => void;
}

const WINDOW_WIDTH = 400;
const WINDOW_HEIGHT = WINDOW_WIDTH * 2; // aspect ratio height:width = 2:1

const WINDOW_FEATURES = [
  `width=${WINDOW_WIDTH}`,
  `height=${WINDOW_HEIGHT}`,
  "toolbar=no",
  "location=no",
  "status=no",
  "menubar=no",
  "scrollbars=yes",
  "resizable=yes",
].join(",");

const CLOSE_POLL_INTERVAL_MS = 500;
const POPUP_INITIALIZED_DATA_KEY = "klvPopupInitialized";
const WINDOW_READY_DELAY_MS = 200;
const FIREFOX_READY_FALLBACK_MS = 350;
const FIREFOX_FOCUS_PARENT_DELAY_MS = 40;
const FIREFOX_FOCUS_BACK_DELAY_MS = 120;
const FIREFOX_FOCUS_FINAL_NOTIFY_DELAY_MS = 220;

const markWindowInitialized = (popupWindow: Window) => {
  popupWindow.document.documentElement.dataset[POPUP_INITIALIZED_DATA_KEY] = "true";
};

const isWindowInitialized = (popupWindow: Window) =>
  popupWindow.document.documentElement.dataset[POPUP_INITIALIZED_DATA_KEY] === "true";

const syncPopupWindow = (popupWindow: Window, title?: string) => {
  if (popupWindow.closed) {
    return;
  }

  const popupDocument = popupWindow.document;
  popupDocument.title = title ?? "KLV Metadata";

  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  if (!isWindowInitialized(popupWindow)) {
    const metaViewport = document.querySelector('meta[name="viewport"]');
    if (metaViewport) {
      popupDocument.head.appendChild(metaViewport.cloneNode(true));
    }

    const styleElements = document.head.querySelectorAll("link[rel=\"stylesheet\"], style");
    styleElements.forEach((node) => {
      if (node instanceof HTMLLinkElement && node.rel === "stylesheet") {
        const linkClone = popupDocument.createElement("link");
        linkClone.rel = "stylesheet";
        linkClone.type = node.type;
        linkClone.href = new URL(node.href, window.location.origin).href;
        if (node.media) linkClone.media = node.media;
        popupDocument.head.appendChild(linkClone);
      } else if (node instanceof HTMLStyleElement) {
        popupDocument.head.appendChild(node.cloneNode(true));
      }
    });

    markWindowInitialized(popupWindow);
  }

  // Keep base styling in sync with the parent document so theming looks correct.
  const parentBodyStyles = window.getComputedStyle(document.body);
  const parentHtmlStyles = window.getComputedStyle(document.documentElement);

  popupDocument.body.style.margin = "0";
  popupDocument.body.style.padding = "0";
  popupDocument.body.style.width = "100%";
  popupDocument.body.style.height = "100%";
  popupDocument.body.style.display = "flex";
  popupDocument.documentElement.style.width = "100%";
  popupDocument.documentElement.style.height = "100%";
  popupDocument.documentElement.style.display = "flex";
  popupDocument.body.style.backgroundColor = parentBodyStyles.backgroundColor || "#182630";
  popupDocument.body.style.color = parentBodyStyles.color || "#E7EEF3";
  popupDocument.body.style.fontFamily = parentBodyStyles.fontFamily || "inherit";
  popupDocument.body.style.fontSize = parentBodyStyles.fontSize || "14px";

  const customProps = Array.from(parentHtmlStyles).filter((prop) => prop.startsWith("--"));
  customProps.forEach((prop) => {
    popupDocument.documentElement.style.setProperty(prop, parentHtmlStyles.getPropertyValue(prop));
  });
};

const KlvWindowPortal = ({ open, onClose, title, children, onReady }: KlvWindowPortalProps) => {
  const [portalState, setPortalState] = useState<{
    externalWindow: Window;
    container: HTMLDivElement;
    cache: ReturnType<typeof createCache>;
  } | null>(null);
  const onCloseRef = useRef(onClose);
  const onReadyRef = useRef(onReady);
  const externalWindowRef = useRef<Window | null>(null);
  const theme = useTheme();
  const closePollRef = useRef<number | null>(null);
  const hasSignaledCloseRef = useRef(false);
  const readinessTimeoutRef = useRef<number | null>(null);
  const [isReady, setIsReady] = useState(false);
  const hasAnnouncedReadyRef = useRef(false);
  const firefoxFallbackTimeoutRef = useRef<number | null>(null);
  const [forceRenderTick, setForceRenderTick] = useState(0);
  const focusParentTimeoutRef = useRef<number | null>(null);
  const focusBackTimeoutRef = useRef<number | null>(null);
  const focusFinalTimeoutRef = useRef<number | null>(null);
  const isFirefox = typeof navigator !== "undefined" && /firefox/i.test(navigator.userAgent);

  const clearFocusTimers = useCallback(() => {
    if (typeof window !== "undefined") {
      if (focusParentTimeoutRef.current !== null) {
        window.clearTimeout(focusParentTimeoutRef.current);
      }
      if (focusBackTimeoutRef.current !== null) {
        window.clearTimeout(focusBackTimeoutRef.current);
      }
      if (focusFinalTimeoutRef.current !== null) {
        window.clearTimeout(focusFinalTimeoutRef.current);
      }
    }
    focusParentTimeoutRef.current = null;
    focusBackTimeoutRef.current = null;
    focusFinalTimeoutRef.current = null;
  }, []);

  const notifyReady = useCallback(() => {
    if (hasAnnouncedReadyRef.current) {
      return;
    }
    hasAnnouncedReadyRef.current = true;
    onReadyRef.current?.();
  }, []);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    if (!open) {
      setPortalState((current) => {
        if (!current) {
          return null;
        }

        const { externalWindow, container } = current;
        try {
          if (!externalWindow.closed) {
            if (container.parentNode === externalWindow.document.body) {
              externalWindow.document.body.removeChild(container);
            }
            externalWindow.close();
          }
        } catch {
          // ignore cross-window cleanup failures
        }

        return null;
      });

      const existing = externalWindowRef.current;
      if (existing && !existing.closed) {
        try {
          existing.close();
        } catch {
          // ignore cross-window cleanup failures
        }
      }
      externalWindowRef.current = null;
      if (typeof window !== "undefined" && readinessTimeoutRef.current !== null) {
        window.clearTimeout(readinessTimeoutRef.current);
      }
      if (typeof window !== "undefined" && firefoxFallbackTimeoutRef.current !== null) {
        window.clearTimeout(firefoxFallbackTimeoutRef.current);
      }
      readinessTimeoutRef.current = null;
      firefoxFallbackTimeoutRef.current = null;
      setIsReady(false);
      hasAnnouncedReadyRef.current = false;
      clearFocusTimers();
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    let popupWindow = externalWindowRef.current;
    if (popupWindow?.closed) {
      popupWindow = null;
    }

    let cancelled = false;
    let rafId: number | null = null;

    if (!popupWindow) {
      popupWindow = window.open("about:blank", "", WINDOW_FEATURES);
      if (!popupWindow) {
        onCloseRef.current?.();
        return;
      }

      popupWindow.document.open();
      popupWindow.document.write("<!DOCTYPE html><html><head></head><body></body></html>");
      popupWindow.document.close();
      externalWindowRef.current = popupWindow;
      try {
        popupWindow.focus();
      } catch {
        // focus may fail if blocked by browser policies
      }
    }

    const prepareWindow = () => {
      if (cancelled || !popupWindow || popupWindow.closed) {
        return;
      }

      const popupDocument = popupWindow.document;
      const { head, body } = popupDocument;

      if (!head || !body) {
        rafId = popupWindow.requestAnimationFrame(prepareWindow);
        return;
      }

      setPortalState((current) => {
        if (current && current.externalWindow === popupWindow) {
          return current;
        }

        const container = popupDocument.createElement("div");
        container.style.width = "100%";
        container.style.height = "100%";
        container.style.display = "flex";
        body.appendChild(container);

        const cache = createCache({
          key: "klv-popup",
          container: head,
        });

        return { externalWindow: popupWindow, container, cache };
      });

      if (typeof window !== "undefined" && readinessTimeoutRef.current !== null) {
        window.clearTimeout(readinessTimeoutRef.current);
        readinessTimeoutRef.current = null;
      }
      if (typeof window !== "undefined" && firefoxFallbackTimeoutRef.current !== null) {
        window.clearTimeout(firefoxFallbackTimeoutRef.current);
        firefoxFallbackTimeoutRef.current = null;
      }
      setIsReady(false);
      hasAnnouncedReadyRef.current = false;
      clearFocusTimers();
    };

    prepareWindow();

    return () => {
      cancelled = true;
      if (rafId !== null && popupWindow && !popupWindow.closed) {
        popupWindow.cancelAnimationFrame(rafId);
      }
    };
  }, [open, clearFocusTimers]);

  useEffect(() => {
    if (!portalState || !open) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    const { externalWindow, container } = portalState;
    if (!externalWindow || externalWindow.closed) {
      return;
    }

    syncPopupWindow(externalWindow, title);

    if (container.parentNode !== externalWindow.document.body) {
      externalWindow.document.body.appendChild(container);
    }

    const handleBeforeUnload = () => {
      onCloseRef.current?.();
    };

    externalWindow.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      externalWindow.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [open, portalState, title, theme]);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && readinessTimeoutRef.current !== null) {
        window.clearTimeout(readinessTimeoutRef.current);
      }
      if (typeof window !== "undefined" && firefoxFallbackTimeoutRef.current !== null) {
        window.clearTimeout(firefoxFallbackTimeoutRef.current);
      }
      const popup = externalWindowRef.current;
      if (popup && !popup.closed) {
        try {
          popup.close();
        } catch {
          // ignore cross-window cleanup failures
        }
      }
      clearFocusTimers();
    };
  }, [clearFocusTimers]);

  // Ensure popup is closed if the main application tab/window is closed or reloaded.
  // This handles cases where React's cleanup effects may not execute before page teardown.
  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        if (externalWindowRef.current && !externalWindowRef.current.closed) {
          externalWindowRef.current.close();
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    if (!open) {
      if (closePollRef.current !== null && typeof window !== "undefined") {
        window.clearTimeout(closePollRef.current);
      }
      closePollRef.current = null;
      hasSignaledCloseRef.current = false;
      if (typeof window !== "undefined" && readinessTimeoutRef.current !== null) {
        window.clearTimeout(readinessTimeoutRef.current);
      }
      if (typeof window !== "undefined" && firefoxFallbackTimeoutRef.current !== null) {
        window.clearTimeout(firefoxFallbackTimeoutRef.current);
      }
      readinessTimeoutRef.current = null;
      firefoxFallbackTimeoutRef.current = null;
      setIsReady(false);
      hasAnnouncedReadyRef.current = false;
      clearFocusTimers();
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    hasSignaledCloseRef.current = false;

    const pollClosed = () => {
      const popup = externalWindowRef.current;
      if (!popup) {
        closePollRef.current = window.setTimeout(pollClosed, CLOSE_POLL_INTERVAL_MS);
        return;
      }

      if (popup.closed) {
        closePollRef.current = null;
        if (!hasSignaledCloseRef.current) {
          hasSignaledCloseRef.current = true;
          setPortalState((current) => (current ? null : current));
          onCloseRef.current?.();
        }
        return;
      }

      closePollRef.current = window.setTimeout(pollClosed, CLOSE_POLL_INTERVAL_MS);
    };

    closePollRef.current = window.setTimeout(pollClosed, CLOSE_POLL_INTERVAL_MS);

    return () => {
      if (closePollRef.current !== null) {
        window.clearTimeout(closePollRef.current);
        closePollRef.current = null;
      }
      hasSignaledCloseRef.current = false;
    };
  }, [open, clearFocusTimers]);

  useEffect(() => {
    if (!open || !portalState) {
      if (typeof window !== "undefined" && readinessTimeoutRef.current !== null) {
        window.clearTimeout(readinessTimeoutRef.current);
      }
      readinessTimeoutRef.current = null;
      setIsReady(false);
      hasAnnouncedReadyRef.current = false;
      clearFocusTimers();
      return;
    }

    if (typeof window === "undefined") {
      setIsReady(true);
      return;
    }

    if (readinessTimeoutRef.current !== null) {
      window.clearTimeout(readinessTimeoutRef.current);
    }

    readinessTimeoutRef.current = window.setTimeout(() => {
      setIsReady(true);
      readinessTimeoutRef.current = null;
    }, WINDOW_READY_DELAY_MS);

    if (firefoxFallbackTimeoutRef.current !== null) {
      window.clearTimeout(firefoxFallbackTimeoutRef.current);
    }

    firefoxFallbackTimeoutRef.current = window.setTimeout(() => {
      // Force re-render tick to retrigger portal content composition once Firefox settles.
      setForceRenderTick((tick) => tick + 1);
      firefoxFallbackTimeoutRef.current = null;
    }, FIREFOX_READY_FALLBACK_MS);

    return () => {
      if (readinessTimeoutRef.current !== null) {
        window.clearTimeout(readinessTimeoutRef.current);
        readinessTimeoutRef.current = null;
      }
      if (firefoxFallbackTimeoutRef.current !== null) {
        window.clearTimeout(firefoxFallbackTimeoutRef.current);
        firefoxFallbackTimeoutRef.current = null;
      }
      clearFocusTimers();
    };
  }, [clearFocusTimers, open, portalState]);

  useEffect(() => {
    if (!isFirefox || !isReady || !portalState) {
      return;
    }

    const { externalWindow } = portalState;

    try {
      externalWindow.focus();
    } catch {
      // ignore focus failures
    }

    try {
      externalWindow.dispatchEvent(new Event("resize"));
    } catch {
      // ignore if resize cannot be dispatched
    }

    try {
      window.dispatchEvent(new Event("resize"));
    } catch {
      // ignore if parent window cannot dispatch resize
    }
  }, [forceRenderTick, isReady, portalState]);

  useEffect(() => {
    if (isFirefox || !isReady || !portalState) {
      return;
    }

    notifyReady();
  }, [isFirefox, isReady, notifyReady, portalState]);

  useEffect(() => {
    if (!isFirefox) {
      return;
    }

    if (!isReady || !portalState) {
      clearFocusTimers();
      return;
    }

    if (hasAnnouncedReadyRef.current) {
      clearFocusTimers();
      return;
    }

    if (typeof window === "undefined") {
      notifyReady();
      return;
    }

    clearFocusTimers();

    const { externalWindow } = portalState;

    focusParentTimeoutRef.current = window.setTimeout(() => {
      try {
        window.focus();
      } catch {
        // ignore focus failures
      }

      try {
        window.dispatchEvent(new Event("resize"));
      } catch {
        // ignore if resize cannot be dispatched
      }

      focusParentTimeoutRef.current = null;

      focusBackTimeoutRef.current = window.setTimeout(() => {
        try {
          externalWindow.focus();
        } catch {
          // ignore focus failures
        }

        try {
          externalWindow.dispatchEvent(new Event("resize"));
        } catch {
          // ignore if resize cannot be dispatched
        }

        focusBackTimeoutRef.current = null;

        focusFinalTimeoutRef.current = window.setTimeout(() => {
          focusFinalTimeoutRef.current = null;
          notifyReady();
          clearFocusTimers();
        }, FIREFOX_FOCUS_FINAL_NOTIFY_DELAY_MS);
      }, FIREFOX_FOCUS_BACK_DELAY_MS);
    }, FIREFOX_FOCUS_PARENT_DELAY_MS);

    return () => {
      clearFocusTimers();
    };
  }, [clearFocusTimers, forceRenderTick, isFirefox, isReady, notifyReady, portalState]);

  useEffect(() => {
    if (!isFirefox || !isReady || !portalState) {
      return;
    }

    const { externalWindow, container } = portalState;

    const forceRepaint = () => {
      const previousDisplay = container.style.display;
      container.style.display = "none";
      // Force synchronous reflow so Firefox paints content without manual focus toggle.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      container.offsetHeight;
      container.style.display = previousDisplay || "flex";
    };

    forceRepaint();

    const timeouts: number[] = [];
    if (typeof externalWindow.setTimeout === "function") {
      // Run a few delayed reflows to catch Firefox when it finally schedules paints.
      timeouts.push(externalWindow.setTimeout(forceRepaint, 32));
      timeouts.push(externalWindow.setTimeout(forceRepaint, 96));
      timeouts.push(externalWindow.setTimeout(forceRepaint, 192));
    }

    return () => {
      timeouts.forEach((timeoutId) => {
        try {
          externalWindow.clearTimeout(timeoutId);
        } catch {
          // ignore
        }
      });
    };
  }, [forceRenderTick, isFirefox, isReady, portalState]);

  if (!open || !portalState) {
    return null;
  }

  const content = isReady ? (
    children
  ) : (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        height: "100%",
        justifyContent: "center",
        width: "100%",
      }}
    >
      <CircularProgress size={28} />
    </Box>
  );

  const portalContent = (
    <CacheProvider value={portalState.cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {/* forceRenderTick nudges Firefox to repaint if it skipped initial render */}
        <Box sx={{ display: "contents" }} key={forceRenderTick}>
          {content}
        </Box>
      </ThemeProvider>
    </CacheProvider>
  );

  return createPortal(portalContent, portalState.container);
};

export default KlvWindowPortal;
